import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { PARAM_SORT, PARAM_SORT_KEYS } from '../../constants'
import type { PARAM_SORT_TYPE } from '../../constants'
import type { components } from '../../constants/schema'

import { HttpReq, toast } from '../../utils'
import { abrViewSubscriptionProductList } from '../../utils/abr'

import { Content } from '../../components/container/Content'
import { Sort } from '../../components/paramOption'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'
import { ProductCard } from './components'

type ProductListResponse = components['schemas']['ResListProduct']

const SORT_LIST: { type: PARAM_SORT_TYPE; title: string }[] = [
  { type: PARAM_SORT_KEYS.DESC, title: '최근순' },
  { type: PARAM_SORT_KEYS.ASC, title: '과거순' },
]

const SIZE = 5

export const MainSaleList = observer(() => {
  const [sortType, setSortType] = useState<PARAM_SORT_TYPE>(PARAM_SORT_KEYS.DESC)
  const [productList, setProductList] = useState<ProductListResponse | null>(null)

  useEffect(() => {
    abrViewSubscriptionProductList()
    ChannelService.track('view_subscription_products_list')
    getProductList({ sortType })
  }, [sortType])

  const getProductList = async ({ sortType, offset: _offset }: { sortType?: PARAM_SORT_TYPE; offset?: number }) => {
    try {
      const sort = sortType ? PARAM_SORT[sortType] : undefined
      const offset = _offset ?? 0

      const response = await HttpReq<ProductListResponse>({
        method: 'GET',
        url: '/product/sales',
        data: { sort, size: SIZE, offset: offset ? offset + SIZE : 0 },
      })

      setProductList((prevState) =>
        prevState && offset ? { ...response, list: [...prevState.list, ...response.list] } : response,
      )
    } catch (err: unknown) {
      toast('작품 목록을 불러오는데 실패했어요.', err)
    }
  }

  const refresh = async () => {
    await getProductList({ sortType })
  }

  const nextProductList = async () => {
    await getProductList({ sortType, offset: (productList?.offset ?? 0) + SIZE })
  }

  return (
    <StyledContent id="infinity-scroll-target" isPullEvent={true} onPull={refresh} backgroundColor="gray_1">
      <ContentsContainer>
        <SortContainer>
          <Sort sortType={sortType} sortList={SORT_LIST} setSortType={setSortType} />
        </SortContainer>
        <InfinityScroll length={productList?.list.length ?? 0} isLast={!!productList?.last} next={nextProductList}>
          <ProductsContainer>
            {productList?.list.map((product) => <ProductCard key={product.id} {...product} />)}
          </ProductsContainer>
        </InfinityScroll>
      </ContentsContainer>
    </StyledContent>
  )
})

const StyledContent = styled(Content)`
  height: calc(100% - 86px);
`

const ContentsContainer = styled.div`
  padding: 0 16px 16px;
  height: auto;
`

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`

const ProductsContainer = styled.article`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
